import { useContext, useEffect, useState } from 'react';
import { HiOutlineLink } from 'react-icons/hi';
import {
  deleteProject,
  postProject,
  putProject,
} from '../../../apis/profile.api';
import {
  handleCheckChangeForm,
  handleCheckFalsyForm,
} from '../../../common/checkForm';
import {
  handleDeleteForm,
  handleResetForm,
  handleSubmitForm,
} from '../../../common/formCallApi';
import {
  handleIsWorking,
  handleValidateUserInfoHaveStartDateAndEndDate,
  renderSubmitFormContent,
} from '../../../common/formCommonFunc';
import {
  listViewPermissionIcon,
  viewPermissionData,
} from '../../../constant/user-setting-modal-resources';
// import { validateTimeLargerThanCurrentDate } from '../../../helpers/validate';
import useIsWorking from '../../../hooks/formGeneralStates/useIsWorking';
import { IModalProps as IProps } from '../../../interfaces/components/modal.interface';
import { IUserProject } from '../../../interfaces/profile.interface';
import FormLayout from '../../../layouts/FormLayout/FormLayout2';
import AuthContext from '../../../state/auth/AuthContext';
import { useModal } from '../../../state/modal-provider/ModalProvider';
import { initProjectState } from '../../../state/user-setting-modal/initState';
import { ModalProfileFieldWithLabel } from '../../elements/ModalProfileFieldWithLabel';
import DateInput from '../../input/DateInput';
import PrimaryInput from '../../input/PrimaryInput';
import PrimaryTextArea from '../../input/PrimaryTextArea';
import SelectInput from '../../input/SelectInput2';

const requiredKeys = [
  'projectName',
  'numberOfMembers',
  'startDate',
  'position',
  'mission',
];

const Project = ({ defaultData, cancelCallback, isModalCancelled }: IProps) => {
  const [userProject, setUserProject] = useState<IUserProject>({
    projectName: defaultData?.projectName ?? '',
    position: defaultData?.position ?? '',
    startDate: defaultData?.startDate ?? '',
    endDate: defaultData?.endDate ?? '',
    viewPermission: defaultData?.viewPermission ?? 'public',
    mission: defaultData?.mission ?? '',
    description: defaultData?.description ?? '',
    numberOfMembers: defaultData?.numberOfMembers ?? '',
    link: defaultData?.link ?? '',
  });

  const [isSubmit, setIsSubmit] = useState<boolean>(false);
  const { isWorking, setIsWorking } = useIsWorking({
    defaultData,
    requiredKeys,
  });

  const { setLoading } = useContext(AuthContext);
  const { toast } = useModal();

  const handleSetState = (name: string, value: string) => {
    setUserProject((state) => ({ ...state, [name]: value }));
  };

  const handleToggleLeaveModal = () => {
    if (
      !handleCheckChangeForm(userProject, {
        projectName: defaultData?.projectName ?? '',
        position: defaultData?.position ?? '',
        startDate: defaultData?.startDate ?? '',
        endDate: defaultData?.endDate ?? '',
        viewPermission: defaultData?.viewPermission ?? 'public',
        mission: defaultData?.mission ?? '',
        description: defaultData?.description ?? '',
        numberOfMembers: defaultData?.numberOfMembers ?? '',
        link: defaultData?.link ?? '',
      })
    ) {
      toast.warning({
        deleteTitle: 'Thoát',
        cancelTitle: 'Ở lại',
        onDelete: () => cancelCallback && cancelCallback(false),
        title: 'Thay đổi chưa lưu',
        content:
          'Thay đổi của bạn chưa được lưu. Bạn vẫn muốn thoát mà không lưu?',
      });
    } else {
      cancelCallback && cancelCallback(false);
    }
  };

  const validateDate = handleValidateUserInfoHaveStartDateAndEndDate({
    state: userProject,
    skipCurrentDateCheck: true,
  });

  const dateError = (_state: any) =>
    validateDate !== 'valid' && validateDate !== 'largerThanCurrentDate';

  const resetForm = () =>
    handleResetForm({
      setValueState: setUserProject,
      initValueState: initProjectState,
    });

  const deleteForm = () =>
    handleDeleteForm({
      runAsyncFunc: !!defaultData?._id,
      callback: () => setIsSubmit(false),
      asyncFunc: deleteProject,
      handleResetForm: resetForm,
      id: defaultData?._id ?? '',
      formCancelCallBack: cancelCallback,
      toggleSuccessModalNoti: () =>
        toast.success({
          content: 'Xoá thông tin thành công.',
        }),
      toggleErrorModalNoti: () =>
        toast.error({
          content: 'Có lỗi xảy ra, vui lòng thử lại.',
        }),
      setLoading,
    });

  const submitForm = () =>
    handleSubmitForm({
      callback: () => setIsSubmit(true),
      data: userProject,
      requiredKeys,
      extraValidate: () => validateDate === 'valid',
      id: defaultData?._id ?? '',
      asyncFunc: !defaultData?._id ? postProject : putProject,
      formCancelCallBack: cancelCallback,
      toggleSuccessModalNoti: () =>
        toast.success({
          content: renderSubmitFormContent(defaultData?._id),
        }),
      toggleErrorModalNoti: () =>
        toast.error({
          content: 'Có lỗi xảy ra, vui lòng thử lại.',
        }),
      setLoading,
    });

  // warning when leave modal
  useEffect(() => {
    if (isModalCancelled) {
      handleToggleLeaveModal();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isModalCancelled]);

  return (
    <FormLayout
      deleteCallback={() =>
        toast.warning({
          title: 'Xoá mục dự án!',
          content: 'Bạn chắc chắn muốn xoá nội dung mục dự án?',
          deleteTitle: 'Xoá',
          cancelTitle: 'Huỷ',
          onDelete: deleteForm,
        })
      }
      saveCallback={submitForm}
      formName="dự án"
      cancelCallback={handleToggleLeaveModal}
      disabledDeletButton={handleCheckFalsyForm(userProject)}
    >
      <div className="sm:py-4 flex flex-col justify-center gap-5">
        <ModalProfileFieldWithLabel labelContent={'Chế độ hiển thị'}>
          <SelectInput
            data={viewPermissionData}
            placeholder="Công khai"
            setValue={handleSetState}
            value={userProject.viewPermission}
            error={false}
            propertyName="viewPermission"
            isObject
            listIcon={listViewPermissionIcon}
          />
        </ModalProfileFieldWithLabel>

        <h1 className=" text-[#333333] text-xl font-bold">Thông tin dự án</h1>

        <ModalProfileFieldWithLabel labelContent={'Tên dự án*'}>
          <PrimaryInput
            desc="Tên dự án"
            value={userProject.projectName}
            setValue={handleSetState}
            inputName="projectName"
            isObject={true}
            error={isSubmit && userProject.projectName === ''}
          />
          {isSubmit && userProject.projectName === '' && (
            <p className="text-red-400 text-sm">
              Vui lòng điền tên dự án của bạn
            </p>
          )}
        </ModalProfileFieldWithLabel>

        <ModalProfileFieldWithLabel
          labelContent={'Số lượng thành viên*'}
          // childrenClassName={'w-[40%]'}
        >
          <PrimaryInput
            desc="Số lượng"
            value={userProject.numberOfMembers}
            setValue={handleSetState}
            inputName="numberOfMembers"
            isObject={true}
            type="number"
            error={isSubmit && !userProject.numberOfMembers}
            classname="w-[40%]"
          />
          {isSubmit && !userProject.numberOfMembers && (
            <p className="text-red-400 text-sm">
              Vui lòng điền số lượng thành viên
            </p>
          )}
        </ModalProfileFieldWithLabel>

        <ModalProfileFieldWithLabel
          labelContent={'Thời gian*'}
          childrenClassName={'gap-5'}
        >
          <article className="flex gap-2 items-center">
            <input
              checked={isWorking === 'on'}
              type="checkbox"
              onClick={(event) =>
                handleIsWorking({
                  event,
                  setIsWorking,
                  handleSetState,
                })
              }
            />
            <p className="text-gray-500 py-2">Hiện đang làm việc</p>
          </article>
          <DateInput
            showIcon={false}
            value={userProject.startDate}
            setValue={handleSetState}
            isObject={true}
            name="startDate"
            max={(userProject.endDate !== '' && userProject.endDate) || ''}
            error={
              isSubmit &&
              (dateError(userProject.startDate) || !userProject.startDate)
            }
            placeholder="Ngày bắt đầu"
            classname="w-[50%]"
          />
          <DateInput
            showIcon={false}
            disabled={isWorking === 'on'}
            value={userProject.endDate}
            setValue={handleSetState}
            isObject={true}
            name="endDate"
            min={userProject.startDate}
            placeholder="Ngày kết thúc"
            error={isSubmit && dateError(userProject.endDate)}
            classname="w-[50%]"
          />
          {isSubmit && validateDate === 'invalidTime' && (
            <p className="text-red-400 -mt-5 -mb-3 text-sm">
              Thời gian bắt đầu không được lớn hơn thời gian kết thúc
            </p>
          )}
          {isSubmit && validateDate === 'notHaveStartDateButEndDate' && (
            <p className="text-red-400 -mt-5 -mb-3 text-sm">
              Nhập thời gian bắt đầu trước khi nhập thời gian kết thúc!
            </p>
          )}
          {isSubmit && validateDate === 'largerThanCurrentDate' && (
            <p className="text-red-400 -mt-5 -mb-3 text-sm">
              Thời gian không được lớn hơn thời gian hiện tại
            </p>
          )}
        </ModalProfileFieldWithLabel>

        <ModalProfileFieldWithLabel labelContent="Mô tả dự án">
          <PrimaryTextArea
            type="text"
            desc="Giới thiệu chi tiết về mục đích và tầm ảnh hưởng của dự án"
            value={userProject.description}
            setValue={handleSetState}
            isObject
            inputName="description"
          />
        </ModalProfileFieldWithLabel>
        <ModalProfileFieldWithLabel labelContent="Liên kết">
          <PrimaryInput
            type="text"
            desc="Dán liên kết liên quan đến hoạt động"
            value={userProject.link}
            setValue={handleSetState}
            isObject
            inputName="link"
            icon={<HiOutlineLink />}
            iconClass="bg-transperant text-[#C4C4C4]"
            // error={isSubmit && !validateLink()}
          />
        </ModalProfileFieldWithLabel>
        <h1 className=" text-[#333333] text-xl font-bold">Vai trò của bạn</h1>
        <ModalProfileFieldWithLabel labelContent="Vị trí tham gia*">
          <PrimaryInput
            desc="Vị trí của bạn trong dự án"
            value={userProject.position}
            setValue={handleSetState}
            inputName="position"
            isObject={true}
            type="text"
            error={isSubmit && userProject.position === '' ? true : false}
            classname="px-3"
          />
          {isSubmit && !userProject.position && (
            <p className="text-red-400 text-sm">Vui lòng điền vị trí của bạn</p>
          )}
        </ModalProfileFieldWithLabel>
        <ModalProfileFieldWithLabel labelContent="Nhiệm vụ*">
          <PrimaryTextArea
            type="text"
            desc="Giới thiệu về vai trò và đóng góp của bạn trong dự án"
            value={userProject.mission}
            setValue={handleSetState}
            isObject
            inputName="mission"
            error={isSubmit && !userProject.mission}
          />
          {isSubmit && !userProject.mission && (
            <p className="text-red-400 text-sm">Vui lòng điền nhiệm vụ</p>
          )}
        </ModalProfileFieldWithLabel>
      </div>
    </FormLayout>
  );
};

export default Project;
